import { useCallback, useMemo } from 'react';

import {
  useDeleteCalendarMeeting,
  useGetCalendarMeeting,
  useGetLastRecurringMeeting,
  useListCalendarMeetings,
  useGetCalendarMeetingInfo
} from '@/api/generated';

const useMeetings = (
  from?: number,
  to?: number,
  meetingId?: string,
  calendarEventId?: string,
  calendarMasterEventId?: string
) => {
  const {
    data: meetingListResponse,
    refetch: refetchMeetings,
    isLoading: meetingListLoading
  } = useListCalendarMeetings(
    { from, to, calendarEventId },
    {
      query: {
        queryKey: ['listCalendarMeetings', from, to, calendarEventId],
        enabled: !!from || !!calendarEventId || !!to
      }
    }
  );

  const meetingList = useMemo(() => meetingListResponse?.items || [], [meetingListResponse?.items]);

  const {
    data: meetingResponse,
    refetch: refetchMeeting,
    isLoading: meetingLoading
  } = useGetCalendarMeeting(meetingId!, {
    query: {
      queryKey: ['getCalendarMeeting', meetingId],
      enabled: !!meetingId
    }
  });

  const meeting = useMemo(() => meetingResponse?.items?.[0] || null, [meetingResponse?.items]);

  const { data: meetingInfoResponse, isLoading: meetingInfoLoading } = useGetCalendarMeetingInfo(
    meetingId!,
    {
      query: {
        queryKey: ['getCalendarMeetingInfo', meetingId],
        enabled: !!meetingId
      }
    }
  );

  const meetingInfo = useMemo(
    () => meetingInfoResponse?.items?.[0] || null,
    [meetingInfoResponse?.items]
  );

  const { data: lastMeetingResponse, isLoading: lastMeetingLoading } = useGetLastRecurringMeeting(
    calendarMasterEventId!,
    {
      query: {
        queryKey: ['getLastMeeting', calendarMasterEventId],
        enabled: !!calendarMasterEventId
      }
    }
  );

  const lastMeeting = useMemo(
    () => lastMeetingResponse?.items?.[0] || null,
    [lastMeetingResponse?.items]
  );

  const { mutateAsync: mutateDeleteCalendarMeeting } = useDeleteCalendarMeeting();

  const deleteMeeting = useCallback(
    async (id: string) => {
      await mutateDeleteCalendarMeeting({ id });
    },
    [mutateDeleteCalendarMeeting]
  );

  return {
    meetingList,
    refetchMeetings,
    meetingListLoading,
    meeting,
    meetingInfo,
    meetingInfoLoading,
    refetchMeeting,
    meetingLoading,
    deleteMeeting,
    lastMeeting,
    lastMeetingLoading
  };
};

export default useMeetings;
